// ============================================================================
// MODAL MINIMIZE CONTEXT — Minimize-to-bar support for form modals
// ============================================================================
// Allows form modals to be minimized to a floating bar at the bottom of the
// content area. Users can navigate to other tabs and resume editing later.
// Always enabled — no user toggle required.

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

// ============================================================================
// TYPES
// ============================================================================

export interface MinimizedModalInfo {
  id: string;
  title: string;        // e.g., "Add New Product" or "Edit - PRD001"
  moduleName: string;   // e.g., "Product Master" — shown as badge in the bar
  tabId: string;        // which tab owns this modal
  onRestore?: () => void; // Called when user clicks Resume — re-opens the modal
  /** Whether this minimized modal has unsaved changes — used for tab-close confirmation */
  isDirty?: boolean;
  /** @internal Stable sort key assigned once on first minimize — preserved across restore/re-minimize */
  _order?: number;
}

export interface ModalMinimizeContextType {
  /** Register a modal as minimized */
  minimize: (info: MinimizedModalInfo) => void;
  /** Restore a minimized modal (navigates to owning tab) */
  restore: (id: string) => void;
  /** Dismiss a minimized modal without restoring */
  dismiss: (id: string) => void;
  /** Check if a modal is currently minimized */
  isMinimized: (id: string) => boolean;
  /** Check if a tab has any minimized modals */
  hasMinimizedModal: (tabId: string) => boolean;
  /** Get a minimized modal by id (stable — safe to call from any closure) */
  getMinimizedModal: (id: string) => MinimizedModalInfo | undefined;
  /** List of all minimized modals */
  minimizedModals: MinimizedModalInfo[];
  /** Whether the feature is enabled */
  enabled: boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const ModalMinimizeContext = createContext<ModalMinimizeContextType>({
  minimize: () => {},
  restore: () => {},
  dismiss: () => {},
  isMinimized: () => false,
  hasMinimizedModal: () => false,
  getMinimizedModal: () => undefined,
  minimizedModals: [],
  enabled: true,
});

// ============================================================================
// PROVIDER
// ============================================================================

/** Control handle exposed via ref for parent components outside the provider */
export interface MinimizeControlHandle {
  getCount: () => number;
  dismissAll: () => void;
}

interface ModalMinimizeProviderProps {
  children: ReactNode;
  onSwitchToTab: (tabId: string) => void;
  /** Ref that exposes dismissByTab — used by parent to clean up on tab close */
  dismissByTabRef?: React.RefObject<((tabId: string) => void) | null>;
  /** Ref that exposes getCount/dismissAll — used by parent for feature toggle */
  controlRef?: React.RefObject<MinimizeControlHandle | null>;
  /** Ref that exposes getDirtyCountByTab — used by parent to check unsaved state before tab close */
  getDirtyCountByTabRef?: React.RefObject<((tabId: string) => number) | null>;
  /** Ref that exposes getTotalDirtyCount — used by parent for logout/beforeunload unsaved checks */
  getTotalDirtyCountRef?: React.RefObject<(() => number) | null>;
}

export const ModalMinimizeProvider: React.FC<ModalMinimizeProviderProps> = ({
  children,
  onSwitchToTab,
  dismissByTabRef,
  controlRef,
  getDirtyCountByTabRef,
  getTotalDirtyCountRef,
}) => {
  const [modals, setModals] = useState<MinimizedModalInfo[]>([]);

  // Ref that always holds the latest modals — allows stable callbacks that
  // never go stale even when captured in old closures (e.g., memoized grid rows)
  const modalsRef = useRef(modals);
  modalsRef.current = modals;

  // Monotonic counter + map that remembers the original insertion order for each
  // modal id.  restore() does NOT clear the order, so re-minimizing the same
  // modal puts it back in its original position.  dismiss() / dismissByTab() /
  // dismissAll() DO clear, because the user explicitly discarded.
  const orderCounter = useRef(0);
  const orderMap = useRef<Map<string, number>>(new Map());

  const minimize = useCallback((info: MinimizedModalInfo) => {
    setModals(prev => {
      // Reuse the original order if this modal was previously minimized, else assign new
      let order = orderMap.current.get(info.id);
      if (order === undefined) {
        order = ++orderCounter.current;
        orderMap.current.set(info.id, order);
      }
      const entry: MinimizedModalInfo = { ...info, _order: order };

      // If already present (shouldn't normally happen), update in place
      const existIdx = prev.findIndex(m => m.id === info.id);
      if (existIdx >= 0) {
        const next = [...prev];
        next[existIdx] = entry;
        return next;
      }

      // Insert at the correct sorted position so order is always stable
      const next = [...prev];
      const insertIdx = next.findIndex(m => (m._order ?? 0) > order!);
      if (insertIdx === -1) {
        next.push(entry);
      } else {
        next.splice(insertIdx, 0, entry);
      }
      return next;
    });
  }, []);

  // Stable restore — reads from ref, no modals dep needed.
  // Does NOT clear orderMap so re-minimizing preserves the original position.
  const restore = useCallback((id: string) => {
    const modal = modalsRef.current.find(m => m.id === id);
    if (!modal) return;
    onSwitchToTab(modal.tabId);
    setModals(prev => prev.filter(m => m.id !== id));
    if (modal.onRestore) {
      modal.onRestore();
    }
  }, [onSwitchToTab]);

  // Removes from strip but preserves orderMap — so re-minimizing the same
  // modal keeps its original position.  Used by screen handlers that
  // dismiss + call onRestore manually.
  const dismiss = useCallback((id: string) => {
    setModals(prev => {
      const next = prev.filter(m => m.id !== id);
      return next.length !== prev.length ? next : prev;
    });
  }, []);

  // Stable — reads from ref, always returns current answer from any closure
  const isMinimized = useCallback((id: string) => {
    return modalsRef.current.some(m => m.id === id);
  }, []);

  // Stable — reads from ref
  const hasMinimizedModal = useCallback((tabId: string) => {
    return modalsRef.current.some(m => m.tabId === tabId);
  }, []);

  // Stable — reads from ref; use in handlers instead of minimizedModals.find()
  const getMinimizedModal = useCallback((id: string) => {
    return modalsRef.current.find(m => m.id === id);
  }, []);

  // Returns the number of minimized modals for a tab that have unsaved changes
  const getDirtyCountByTab = useCallback((tabId: string) => {
    return modalsRef.current.filter(m => m.tabId === tabId && m.isDirty).length;
  }, []);

  // Returns the total number of dirty minimized modals across ALL tabs
  const getTotalDirtyCount = useCallback(() => {
    return modalsRef.current.filter(m => m.isDirty).length;
  }, []);

  // Expose dismissByTab via ref for parent components (e.g., handleTabClose)
  const dismissByTab = useCallback((tabId: string) => {
    // Clear order tracking for all modals of this tab
    modalsRef.current.forEach(m => {
      if (m.tabId === tabId) orderMap.current.delete(m.id);
    });
    setModals(prev => {
      const next = prev.filter(m => m.tabId !== tabId);
      return next.length !== prev.length ? next : prev;
    });
  }, []);

  const dismissAll = useCallback(() => {
    orderMap.current.clear();
    setModals(prev => prev.length > 0 ? [] : prev);
  }, []);

  useEffect(() => {
    if (dismissByTabRef) {
      dismissByTabRef.current = dismissByTab;
    }
    return () => {
      if (dismissByTabRef) {
        dismissByTabRef.current = null;
      }
    };
  }, [dismissByTabRef, dismissByTab]);

  useEffect(() => {
    if (getDirtyCountByTabRef) {
      getDirtyCountByTabRef.current = getDirtyCountByTab;
    }
    return () => {
      if (getDirtyCountByTabRef) {
        getDirtyCountByTabRef.current = null;
      }
    };
  }, [getDirtyCountByTabRef, getDirtyCountByTab]);

  useEffect(() => {
    if (getTotalDirtyCountRef) {
      getTotalDirtyCountRef.current = getTotalDirtyCount;
    }
    return () => {
      if (getTotalDirtyCountRef) {
        getTotalDirtyCountRef.current = null;
      }
    };
  }, [getTotalDirtyCountRef, getTotalDirtyCount]);

  useEffect(() => {
    if (controlRef) {
      controlRef.current = {
        getCount: () => modals.length,
        dismissAll,
      };
    }
    return () => {
      if (controlRef) {
        controlRef.current = null;
      }
    };
  }, [controlRef, modals.length, dismissAll]);

  return (
    <ModalMinimizeContext.Provider
      value={{ minimize, restore, dismiss, isMinimized, hasMinimizedModal, getMinimizedModal, minimizedModals: modals, enabled: true }}
    >
      {children}
    </ModalMinimizeContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const useModalMinimize = (): ModalMinimizeContextType => {
  return useContext(ModalMinimizeContext);
};

export default ModalMinimizeContext;
